import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import NavigationMenu from '../../components/feature/NavigationMenu';
import NewsletterSignup from './components/NewsletterSignup';
import RewardTiers from './components/RewardTiers';

export default function HomePage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  
  const slides = [
    {
      type: 'video',
      content: 'https://www.youtube.com/embed/4Xf3McA6dVU?si=jPB8OAnXPrwqQzsx&autoplay=1&mute=1&loop=1&playlist=4Xf3McA6dVU&enablejsapi=1',
      title: 'Marine Pad: A Gift from the Sea',
      subtitle: 'A Revolution for Our Bodies'
    },
    {
      type: 'image',
      content: 'https://public.readdy.ai/ai/img_res/43427064d544dc5c1094a602547f4d9c.jpg',
      title: "Nature's Solution",
      subtitle: 'Bio-SA Technology from the Ocean'
    },
    {
      type: 'image', 
      content: 'https://readdy.ai/api/search-image?query=Modern%20laboratory%20with%20marine%20biotechnology%20research%2C%20scientists%20working%20with%20chitin%20nano-fibers%2C%20sustainable%20product%20development%2C%20eco-friendly%20innovation%2C%20clean%20scientific%20environment%2C%20marine%20polymer%20research&width=1920&height=800&seq=hero-slide-3&orientation=landscape',
      title: 'Science Meets Sustainability',
      subtitle: 'Breakthrough Bio-SA Innovation'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      if (currentSlide !== 0 || !isVideoPlaying) {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }
    }, 8000);
    return () => clearInterval(timer);
  }, [slides.length, currentSlide, isVideoPlaying]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleVideoClick = () => {
    const iframe = document.querySelector('iframe');
    if (iframe) {
      setIsVideoPlaying(!isVideoPlaying);
      iframe.contentWindow?.postMessage(
        `{"event":"command","func":"${isVideoPlaying ? 'pauseVideo' : 'playVideo'}","args":""}`,
        '*'
      );
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <header className="absolute top-0 left-0 right-0 z-50 bg-gradient-to-r from-teal-600/90 to-blue-600/90 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold text-white" style={{ fontFamily: '"Pacifico", serif' }}>
              Marine Pad
            </Link>
            <NavigationMenu />
            <Link
              to="/crowdfunding"
              className="bg-white text-teal-600 px-6 py-2 rounded-full font-semibold hover:bg-teal-50 transition-colors whitespace-nowrap"
            >
              Support Us
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Carousel Section */}
      <section className="relative h-screen overflow-hidden">
        <div className="relative w-full h-full">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {slide.type === 'video' ? (
                <div className="relative w-full h-full">
                  <iframe
                    src={slide.content}
                    className="w-full h-full object-cover cursor-pointer"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="Marine Pad Video"
                    onClick={handleVideoClick}
                  ></iframe>
                  <div className="absolute inset-0 bg-black bg-opacity-40 pointer-events-none"></div>
                  {/* Video Control Overlay */}
                  <div 
                    className="absolute inset-0 flex items-center justify-center cursor-pointer"
                    onClick={handleVideoClick}
                  >
                    <div className={`w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-opacity ${isVideoPlaying ? 'opacity-0 hover:opacity-100' : 'opacity-100'}`}>
                      <i className={`${isVideoPlaying ? 'ri-pause-line' : 'ri-play-line'} text-3xl text-white`}></i>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="w-full h-full bg-cover bg-center"
                  style={{
                    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('${slide.content}')`
                  }}
                ></div>
              )}
              
              {/* Content Overlay */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center text-white max-w-4xl mx-auto px-4">
                  <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                    {slide.title}
                  </h1>
                  <p className="text-2xl md:text-3xl mb-8 font-light opacity-90">
                    {slide.subtitle}
                  </p>
                  {index === currentSlide && (
                    <div className="space-y-4 animate-fade-in pointer-events-auto">
                      <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed">
                        Have you ever felt forced to choose between your comfort and a healthy planet? 
                        We looked at this system and said: no more compromises.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/crowdfunding" className="bg-teal-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                          Back Our Campaign
                        </Link>
                        <Link to="/sap-dangers" className="bg-white/20 backdrop-blur-sm text-white border-2 border-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/30 transition-colors whitespace-nowrap cursor-pointer">
                          Learn About SAP Dangers
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Carousel Controls */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors cursor-pointer z-10"
        >
          <i className="ri-arrow-left-line text-xl"></i>
        </button>
        
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors cursor-pointer z-10"
        >
          <i className="ri-arrow-right-line text-xl"></i>
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3 z-10">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-colors cursor-pointer ${
                index === currentSlide ? 'bg-white' : 'bg-white/50'
              }`}
            ></button>
          ))}
        </div>
      </section>

      {/* Kickstarter Reward Tiers */}
      <RewardTiers />

      {/* Our Story Section */}
      <section id="story" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Our Story: A Personal Quest for a Better Way
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                I'm Yun-Young Heo, founder of Marine Pad. As an engineer and entrepreneur, I kept asking: 
                how could an industry serving billions still lack a truly sustainable, body-kind solution?
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                With a team of scientists, including Professor Yung-Bum Seo, we sought not an incremental tweak, 
                but a genuine breakthrough. Could nature itself hold the answer?
              </p>
              <div className="bg-teal-50 p-6 rounded-lg border-l-4 border-teal-600">
                <p className="text-teal-800 font-semibold">
                  "After years of research, we found it: chitin, the second most abundant biopolymer on Earth, 
                  found in crustacean shells."
                </p>
              </div>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Female%20scientist%20and%20engineer%20Yun-Young%20Heo%20in%20modern%20laboratory%2C%20marine%20biotechnology%20research%2C%20chitin%20extraction%20process%2C%20sustainable%20innovation%2C%20professional%20portrait%2C%20scientific%20breakthrough%2C%20eco-friendly%20product%20development&width=600&height=500&seq=founder-story&orientation=portrait"
                alt="Yun-Young Heo, Founder of Marine Pad"
                className="rounded-lg shadow-lg object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Product Section */}
      <section id="product" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Introducing Marine Pad: Period Care Without Compromise
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The world's first 100% biodegradable, marine-based super-absorbent pad that doesn't make you choose 
              between performance and planet.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="text-center p-8 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-6">
                <i className="ri-drop-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Super-Absorbent</h3>
              <p className="text-gray-700">
                The Bio-SA core draws in and locks away fluid—keeping you dry, confident, and secure.
              </p>
            </div>

            <div className="text-center p-8 bg-green-50 rounded-lg">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-6">
                <i className="ri-heart-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Body-Safe by Design</h3>
              <p className="text-gray-700">
                100% organic-cotton top sheet. ZERO chemical SAP, added fragrances, or unnecessary additives.
              </p>
            </div>

            <div className="text-center p-8 bg-teal-50 rounded-lg">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-6">
                <i className="ri-earth-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Planet-Forward</h3>
              <p className="text-gray-700">
                From pad to wrapper, designed to be 100% biodegradable, returning to nature without a trace.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">How Bio-SA Works</h3>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Bio-SA's nano-fibers wick fluid fast into a capillary network and hold it evenly, 
                so you feel drier with less bulk—without brittle gels.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full mr-4 mt-1">
                    <i className="ri-check-line text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Top: Certified organic cotton</h4>
                    <p className="text-gray-600">Gentle on sensitive skin</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full mr-4 mt-1">
                    <i className="ri-check-line text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Core: Bio-SA marine nano-fiber</h4>
                    <p className="text-gray-600">Revolutionary absorbent technology</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full mr-4 mt-1">
                    <i className="ri-check-line text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Back & wrapper: Biodegradable films</h4>
                    <p className="text-gray-600">Complete environmental responsibility</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Cross-section%20diagram%20of%20Marine%20Pad%20showing%20Bio-SA%20nano-fiber%20technology%2C%20organic%20cotton%20top%20layer%2C%20biodegradable%20materials%2C%20scientific%20product%20illustration%2C%20sustainable%20feminine%20hygiene%20innovation%2C%20technical%20product%20design&width=600&height=500&seq=product-tech&orientation=landscape"
                alt="Marine Pad Technology"
                className="rounded-lg shadow-lg object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section id="comparison" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Clear Comparison
            </h2>
            <p className="text-xl text-gray-600">
              See how Marine Pad stands apart from conventional and other "organic" alternatives
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-teal-600 to-blue-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left font-semibold">Feature</th>
                    <th className="px-6 py-4 text-center font-semibold">Marine Pad</th>
                    <th className="px-6 py-4 text-center font-semibold">Conventional Pads</th>
                    <th className="px-6 py-4 text-center font-semibold">Other "Organic" Pads</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-gray-900">100% biodegradable absorbent</td>
                    <td className="px-6 py-4 text-center text-green-600 font-bold">✅</td>
                    <td className="px-6 py-4 text-center text-red-600 font-bold">❌</td>
                    <td className="px-6 py-4 text-center text-red-600 font-bold">❌</td>
                  </tr>
                  <tr className="bg-white">
                    <td className="px-6 py-4 font-semibold text-gray-900">No chemical SAP</td>
                    <td className="px-6 py-4 text-center text-green-600 font-bold">✅</td>
                    <td className="px-6 py-4 text-center text-red-600 font-bold">❌</td>
                    <td className="px-6 py-4 text-center text-red-600 font-bold">❌</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-gray-900">Premium organic cotton topsheet</td>
                    <td className="px-6 py-4 text-center text-green-600 font-bold">✅</td>
                    <td className="px-6 py-4 text-center text-red-600 font-bold">❌</td>
                    <td className="px-6 py-4 text-center text-green-600 font-bold">✅</td>
                  </tr>
                  <tr className="bg-white">
                    <td className="px-6 py-4 font-semibold text-gray-900">High, reliable absorbency</td>
                    <td className="px-6 py-4 text-center text-green-600 font-bold">✅</td>
                    <td className="px-6 py-4 text-center text-green-600 font-bold">✅</td>
                    <td className="px-6 py-4 text-center text-yellow-600 font-bold">❓</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-gray-900">Safe for body & planet (holistic)</td>
                    <td className="px-6 py-4 text-center text-green-600 font-bold">✅</td>
                    <td className="px-6 py-4 text-center text-red-600 font-bold">❌</td>
                    <td className="px-6 py-4 text-center text-yellow-600 font-bold">❓</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Why Kickstarter Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Why We're on Kickstarter
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              We've proven the science and built the product. To challenge an entrenched industry and reach people everywhere, 
              we're inviting you to help us begin at scale.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="text-center p-8 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-6">
                <i className="ri-factory-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Scale Production</h3>
              <p className="text-gray-700">
                Fund the first major production run so pricing stays fair and access broad.
              </p>
            </div>

            <div className="text-center p-8 bg-green-50 rounded-lg">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-6">
                <i className="ri-award-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Global Certifications</h3>
              <p className="text-gray-700">
                Secure USDA Biobased, GOTS, and other certifications for third-party validation.
              </p>
            </div>

            <div className="text-center p-8 bg-teal-50 rounded-lg">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-6">
                <i className="ri-community-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Build Movement</h3>
              <p className="text-gray-700">
                Create education and community so no one must choose between personal health and planetary health.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-teal-600 to-blue-600 text-white p-8 rounded-lg text-center">
            <h3 className="text-2xl font-bold mb-4">Roadmap & Stretch Goals</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
              <div>
                <h4 className="font-semibold mb-2">Phase 1</h4>
                <p>Launch Petite, Regular, and Night sizes</p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Phase 2</h4>
                <p>Develop biodegradable pantyliners using Bio-SA core</p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Phase 3</h4>
                <p>Pilot buy-one-give-one program to combat period poverty</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Is Marine Pad compostable at home?
              </h3>
              <p className="text-gray-700">
                Conditions vary; industrial or community composting works best. Third-party testing is underway 
                to provide specific home composting guidelines.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Any seafood allergens?
              </h3>
              <p className="text-gray-700">
                Chitin is highly purified and does not retain food proteins; final products do not contain shellfish meat. 
                If you have severe allergies, consult your physician.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Odor control without fragrances?
              </h3>
              <p className="text-gray-700">
                The Bio-SA structure and breathable design help reduce moisture and odor without masking scents 
                or adding potentially irritating fragrances.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <NewsletterSignup />

      {/* Final CTA Section */}
      <section id="support" className="py-20 bg-gradient-to-r from-teal-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            This isn't just a launch. It's the start of a new standard.
          </h2>
          <p className="text-xl mb-8 leading-relaxed">
            Be among the first to experience Marine Pad and help accelerate safer, smarter period care. 
            Your pledge tells the world that comfort, performance, and sustainability can—and should—coexist.
          </p>
          <div className="space-y-4">
            <a href="#" className="inline-block bg-white text-teal-600 px-12 py-4 rounded-full text-xl font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              Back Marine Pad Today
            </a>
            <p className="text-lg opacity-90">
              Thank you for believing in a better way.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h4 className="text-2xl font-bold mb-4" style={{ fontFamily: '"Pacifico", serif' }}>
                Marine Pad
              </h4>
              <p className="text-gray-400">
                A gift from the sea, a revolution for our bodies.
              </p>
            </div>
            <div>
              <h5 className="font-bold mb-4">Product</h5>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/#product" className="hover:text-white transition-colors cursor-pointer">How It Works</a></li>
                <li><a href="/sap-dangers" className="hover:text-white transition-colors cursor-pointer">SAP Dangers</a></li>
                <li><a href="/#comparison" className="hover:text-white transition-colors cursor-pointer">Comparison</a></li>
              </ul>
            </div>
            <div>
              <h5 className="font-bold mb-4">Company</h5>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/#story" className="hover:text-white transition-colors cursor-pointer">Our Story</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Team</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Contact</a></li>
              </ul>
            </div>
            <div>
              <h5 className="font-bold mb-4">Support</h5>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/#faq" className="hover:text-white transition-colors cursor-pointer">FAQ</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Shipping</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Returns</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Marine Pad. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
